import { useState, useEffect, type FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  type AuthError,
} from 'firebase/auth';
import { auth, IS_FIREBASE_CONFIGURED } from '../config/firebase';
import { api, ApiError } from '../lib/api';
import { isPasswordValid } from '../lib/password';
import { PasswordChecklist } from '../components/ui/PasswordChecklist';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

const inputClass =
  'w-full rounded-lg border border-stone bg-white px-4 py-3 text-sm ' +
  'text-midnight placeholder:text-slate outline-none ' +
  'focus:border-plum focus:ring-2 focus:ring-plum/20 transition-all duration-150';

const inputErrorClass =
  'w-full rounded-lg border border-red-400 bg-white px-4 py-3 text-sm ' +
  'text-midnight placeholder:text-slate outline-none ' +
  'focus:border-red-400 focus:ring-2 focus:ring-red-200 transition-all duration-150';

function parseResetError(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  const code = (err as AuthError)?.code;
  switch (code) {
    case 'auth/expired-action-code':
      return 'This reset link has expired. Please request a new one.';
    case 'auth/invalid-action-code':
      return 'This reset link is invalid or has already been used.';
    case 'auth/weak-password':
      return 'Password must be at least 8 characters and contain a letter and a number.';
    default:
      return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
  }
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const customToken = searchParams.get('token') ?? '';
  const oobCode = searchParams.get('oobCode') ?? '';

  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState('');
  const [verifyError, setVerifyError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Verify the token/code on mount
  useEffect(() => {
    if (customToken) {
      // Custom token — just show the form (token is validated on submit)
      setEmail('');
      setVerifying(false);
      return;
    }

    if (oobCode) {
      // Legacy Firebase oobCode
      if (!IS_FIREBASE_CONFIGURED) {
        setEmail('dev@example.com');
        setVerifying(false);
        return;
      }
      verifyPasswordResetCode(auth, oobCode)
        .then(userEmail => {
          setEmail(userEmail);
          setVerifying(false);
        })
        .catch(err => {
          setVerifyError(parseResetError(err));
          setVerifying(false);
        });
      return;
    }

    setVerifyError('Missing reset code. Please use the link from your email.');
    setVerifying(false);
  }, [customToken, oobCode]);

  const passwordsMatch = password === confirmPassword;
  const passwordValid = isPasswordValid(password);
  const canSubmit = !submitting && passwordValid && passwordsMatch && !!password && !!confirmPassword;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (!isPasswordValid(password)) {
      setError('Password does not meet the requirements.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      if (customToken) {
        // Custom token flow — call our backend
        const res = await api.post<{ success: boolean; data?: { email: string }; error?: string }>(
          '/auth/reset-password-with-token',
          { token: customToken, newPassword: password },
        );
        if (res.data?.email) setEmail(res.data.email);
      } else if (oobCode && IS_FIREBASE_CONFIGURED) {
        // Legacy Firebase oobCode flow
        await confirmPasswordReset(auth, oobCode, password);
      }
      setSuccess(true);
    } catch (err) {
      setError(parseResetError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-mist flex flex-col items-center justify-start pt-12 px-5 pb-12">
      <div className="w-full max-w-sm flex flex-col gap-6">

        {/* Hero */}
        <div className="text-center px-2 pt-4 pb-2">
          <h1 className="font-display text-display text-midnight leading-tight">
            Reset{' '}
            <span className="font-extrabold text-plum">Password</span>
          </h1>
        </div>

        <div className="bg-white rounded-2xl shadow-brand-md border border-stone/60 px-5 py-6">
          {verifying && (
            <div className="flex flex-col items-center gap-3 py-6">
              <LoadingSpinner size="md" />
              <p className="text-sm text-slate">Validating reset link...</p>
            </div>
          )}

          {!verifying && verifyError && (
            <div className="flex flex-col gap-5">
              <p className="text-sm text-red-600">{verifyError}</p>
              <button
                type="button"
                onClick={() => navigate('/', { replace: true })}
                className="btn-primary w-full"
              >
                Back to Log In
              </button>
            </div>
          )}

          {!verifying && !verifyError && success && (
            <div className="flex flex-col gap-5">
              <div>
                <h2 className="font-display text-h2 font-semibold text-midnight">
                  Password Updated
                </h2>
                <p className="text-sm text-slate mt-2 leading-relaxed">
                  Your password has been reset{email ? ` for ${email}` : ''}. You can now log in with your new password.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/', { replace: true })}
                className="btn-primary w-full"
              >
                Go to Log In
              </button>
            </div>
          )}

          {!verifying && !verifyError && !success && (
            <div className="flex flex-col gap-5">
              <div>
                <h2 className="font-display text-h2 font-semibold text-midnight">
                  Choose a New Password
                </h2>
                {email && (
                  <p className="text-sm text-slate mt-1 leading-relaxed">
                    For <span className="font-semibold text-midnight">{email}</span>
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="reset-pw" className="field-label">New Password</label>
                  <input
                    id="reset-pw"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Choose a password"
                    className={inputClass}
                  />
                  <PasswordChecklist password={password} />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="confirm-reset-pw" className="field-label">Confirm Password</label>
                  <input
                    id="confirm-reset-pw"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className={confirmPassword && !passwordsMatch ? inputErrorClass : inputClass}
                  />
                  {confirmPassword && !passwordsMatch && (
                    <p className="text-[13px] text-red-500">Passwords do not match.</p>
                  )}
                </div>

                {error && (
                  <p role="alert" className="text-sm text-red-600">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="btn-primary w-full"
                >
                  {submitting ? 'Resetting…' : 'Reset Password'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
